import React from 'react';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
  AlertDialogAction,
} from '@/components/ui/alert-dialog';
import { format, parseISO, addHours } from 'date-fns';
import { AlertTriangle, Clock, Building2 } from 'lucide-react';
import type { BookingConflict } from '@/lib/booking-utils';
import type { Room } from './BookingCalendar';
import { cn } from '@/lib/utils';

interface ConflictWarningDialogProps {
  conflict: BookingConflict;
  rooms: Room[];
  open: boolean;
  onClose: () => void;
  onOverride?: () => void;
  onSelectAlternative: (date: string, time: string, roomId?: number) => void;
}

export function ConflictWarningDialog({
  conflict,
  rooms,
  open,
  onClose,
  onOverride,
  onSelectAlternative
}: ConflictWarningDialogProps) {
  const getRoomName = (roomId: number) => {
    return rooms.find(r => r.id === roomId)?.name || 'Unknown Room';
  };

  const formatTimeRange = (date: string, time: string, duration: number) => {
    const start = parseISO(`${date}T${time}`);
    const end = addHours(start, duration);
    return `${format(start, 'HH:mm')} - ${format(end, 'HH:mm')}`;
  };

  return (
    <AlertDialog open={open} onOpenChange={onClose}>
      <AlertDialogContent className="max-w-2xl">
        <AlertDialogHeader>
          <AlertDialogTitle className="flex items-center gap-2 text-amber-600">
            <AlertTriangle className="h-5 w-5" />
            Booking Conflict Detected
          </AlertDialogTitle>
          <AlertDialogDescription className="text-base">
            {conflict.message}
          </AlertDialogDescription>
        </AlertDialogHeader>

        <div className="my-4 space-y-4">
          <div className="bg-amber-50 p-4 rounded-lg border border-amber-200">
            <h3 className="font-medium text-amber-800 mb-2">Conflicting Booking Details:</h3>
            <div className="space-y-2 text-amber-700">
              <div className="flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                <span>{getRoomName(conflict.booking.roomId)}</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4" />
                <span>
                  {formatTimeRange(
                    conflict.booking.date,
                    conflict.booking.timeSlot,
                    conflict.booking.duration
                  )}
                </span>
              </div>
            </div>
          </div>

          {conflict.suggestedAlternatives && conflict.suggestedAlternatives.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium text-gray-900">Available Alternatives:</h3>
              <div className="grid gap-2">
                {conflict.suggestedAlternatives.map((alt, index) => (
                  <button
                    key={index}
                    onClick={() => onSelectAlternative(alt.date, alt.time, alt.room)}
                    className={cn(
                      "w-full text-left px-4 py-3 rounded-lg",
                      "bg-blue-50 border border-blue-200",
                      "hover:bg-blue-100 hover:border-blue-300",
                      "transition-colors duration-200"
                    )}
                  >
                    <div className="flex items-center justify-between">
                      <div className="space-y-1">
                        <div className="font-medium text-blue-900">
                          {format(parseISO(alt.date), 'EEEE, MMMM d, yyyy')}
                        </div>
                        <div className="text-blue-700">
                          {alt.time} {alt.room && `• ${getRoomName(alt.room)}`}
                        </div>
                      </div>
                      <div className="text-blue-600">
                        Select
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>

        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          {onOverride && (
            <AlertDialogAction
              onClick={onOverride}
              className="bg-amber-600 text-white hover:bg-amber-700"
            >
              Override Conflict
            </AlertDialogAction>
          )}
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}